import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Text, VStack, HStack, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { useFrontendLogger } from '../../atoms/v3/debugAtoms';
import { LongPressRecordButton } from './components/audio';
import VoiceVisualizerTestMinimal from './components/audio/VoiceVisualizerTestMinimal';
import LongPressRecordButtonTest from './components/audio/LongPressRecordButtonTest';

export const AudioRecorderTest: React.FC = () => {
  const logger = useFrontendLogger();
  const [recordings, setRecordings] = useState<Array<{
    id: string;
    blob: Blob;
    duration: number;
    timestamp: number;
    url: string;
    actualDuration?: number; // 从音频文件读取的实际时长
    transcription?: string; // 🔥 新增：转录文本
  }>>([]);
  const [enableSTT, setEnableSTT] = useState(true); // 🔥 新增：STT开关，默认启用

  // 🔥 新增：处理转录完成
  const handleTranscriptionComplete = useCallback((text: string, recordingId?: string) => {
    logger.info('AudioRecorderTest: 🎯 STT转录完成', { text, recordingId });
    console.log('🎯 STT转录结果:', text);

    // 如果有recordingId，更新对应录音的转录文本
    if (recordingId) {
      setRecordings(prev => prev.map(recording =>
        recording.id === recordingId
          ? { ...recording, transcription: text }
          : recording
      ));
    }
  }, [logger]);

  // 清空录音列表
  const handleClearRecordings = useCallback(() => {
    // 清理所有 URL 对象，释放内存
    recordings.forEach(recording => {
      URL.revokeObjectURL(recording.url);
    });
    setRecordings([]);
    logger.info('AudioRecorderTest: Recordings cleared');
  }, [recordings, logger]);


  // 组件卸载时清理所有 URL
  useEffect(() => {
    return () => {
      recordings.forEach(recording => {
        URL.revokeObjectURL(recording.url);
      });
    };
  }, [recordings]);

  return (
    <Box p={6} maxW="800px" mx="auto">
      <VStack spacing={6} align="stretch">
        <Text fontSize="2xl" fontWeight="bold" textAlign="center">
          音频录音组件测试
        </Text>

        <Tabs>
          <TabList>
            <Tab>长按录音组件</Tab>
            <Tab>改造后的录音按钮</Tab>
            <Tab>最终解决方案测试</Tab>
          </TabList>

          <TabPanels>

            {/* 长按录音组件测试 */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Box p={4} borderWidth={1} borderRadius="md" bg="blue.50">
                  <Text fontSize="lg" fontWeight="bold" mb={4}>
                    长按录音组件测试
                  </Text>

                  <VStack spacing={4} align="stretch">
                    {/* 长按录音按钮 */}
                    <Box>
                      <Text fontSize="md" fontWeight="medium" mb={2}>
                        长按录音按钮 (微信风格，全屏覆盖)
                      </Text>
                      <LongPressRecordButton
                        enableSTT={enableSTT} // 🔥 启用STT功能
                        onTranscriptionComplete={(text) => {
                          // 找到最新的录音并更新转录文本
                          setRecordings(prev => {
                            if (prev.length > 0) {
                              const latest = prev[prev.length - 1];
                              return prev.map(recording =>
                                recording.id === latest.id
                                  ? { ...recording, transcription: text }
                                  : recording
                              );
                            }
                            return prev;
                          });
                          handleTranscriptionComplete(text);
                        }}
                        onRecordingComplete={(blob, duration) => {
                          logger.info('LongPressRecordButton: Recording completed', {
                            size: blob.size,
                            duration,
                            type: blob.type
                          });

                          // 添加到录音列表
                          const url = URL.createObjectURL(blob);
                          const newRecording = {
                            id: `longpress_${Date.now()}`,
                            blob,
                            duration,
                            timestamp: Date.now(),
                            url
                          };
                          setRecordings(prev => [...prev, newRecording]);
                        }}
                        onRecordingCancel={() => {
                          logger.info('LongPressRecordButton: Recording cancelled');
                        }}
                        onRecordingEdit={(blob, duration) => {
                          logger.info('LongPressRecordButton: Recording for edit', {
                            size: blob.size,
                            duration,
                            type: blob.type
                          });

                          // 这里可以处理转文字编辑的逻辑
                          alert(`录音转文字编辑功能 - 时长: ${Math.round(duration / 1000 * 10) / 10}秒`);
                        }}
                      />
                    </Box>

                    {/* STT控制 */}
                    <Box p={3} bg="blue.100" borderRadius="md">
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="bold">🎯 STT功能:</Text>
                        <Button
                          size="sm"
                          colorScheme={enableSTT ? "green" : "gray"}
                          onClick={() => setEnableSTT(!enableSTT)}
                        >
                          {enableSTT ? "✅ STT已启用" : "❌ STT已禁用"}
                        </Button>
                      </HStack>
                      <Text fontSize="sm" color="blue.800">
                        启用后，录音完成时会自动调用新的STT API进行转录，结果显示在录音列表中
                      </Text>
                    </Box>

                    <Box p={3} bg="yellow.100" borderRadius="md">
                      <Text fontSize="sm" color="gray.700">
                        <strong>使用说明:</strong><br/>
                        • 桌面端: 点击开始/停止录音<br/>
                        • 移动端: 长按录音，松开发送<br/>
                        • 支持向上拖拽手势: 左侧取消录音，右侧转文字编辑<br/>
                        • 录音时会显示微信风格的全屏覆盖界面<br/>
                        • 🎯 STT功能: 录音完成后自动转录为文字
                      </Text>
                    </Box>
                  </VStack>
                </Box>

                {/* 显示录音列表 */}
                {recordings.length > 0 && (
                  <Box p={4} borderWidth={1} borderRadius="md">
                    <HStack justify="space-between" mb={3}>
                      <Text fontSize="lg" fontWeight="semibold">录音列表 ({recordings.length})</Text>
                      <Button size="sm" onClick={handleClearRecordings}>清空</Button>
                    </HStack>
                    <VStack spacing={3} align="stretch">
                      {recordings.map((recording) => (
                        <Box key={recording.id} p={4} borderWidth={1} borderRadius="md" bg="gray.50">
                          <VStack align="stretch" spacing={3}>
                            <Text fontSize="sm" fontWeight="medium">
                              {recording.id}
                            </Text>
                            <audio controls style={{ width: '100%' }}>
                              <source src={recording.url} type={recording.blob.type} />
                            </audio>
                            {/* 🔥 显示转录结果 */}
                            {recording.transcription && (
                              <Box p={3} bg="purple.50" borderRadius="md" border="1px solid" borderColor="purple.200">
                                <Text fontSize="sm" fontWeight="bold" mb={1} color="purple.800">
                                  🎯 转录结果:
                                </Text>
                                <Text fontSize="sm" color="purple.700">
                                  {recording.transcription}
                                </Text>
                              </Box>
                            )}
                            {/* 🔥 显示录音信息 */}
                            <Text fontSize="xs" color="gray.600">
                              大小: {recording.blob.size} bytes | 时长: {Math.round(recording.duration / 1000 * 10) / 10}s | 时间: {new Date(recording.timestamp).toLocaleTimeString()}
                            </Text>
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </TabPanel>

            {/* 改造后的录音按钮测试 */}
            <TabPanel>
              <LongPressRecordButtonTest />
            </TabPanel>

            {/* 最终解决方案测试 */}
            <TabPanel>
              <VoiceVisualizerTestMinimal />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
};
